
import { Vue, Options } from "vue-class-component";
import {
  ellipsisString,
  formatDateWithoutTime,
  formatTimeCustom,
  isMigrateFromElexys
} from "@/app/infrastructures/misc/Utils";
import formatByCountry, {
  resolveCountry
} from "@/app/infrastructures/misc/common-library/FormatByCountry";
import { PodDexController } from "@/app/ui/controllers/PodDexController";
import ModalForm from "./component/update/update.vue";
import EmptyState from "@/app/ui/components/empty-list/index.vue";
import Notification from "@/app/ui/components/notification/index.vue";
import { Pagination } from "@/domain/entities/Pagination";
import { FlagsPermissionPodDex } from "@/feature-flags/flags-pod-dex";

@Options({
  components: {
    ModalForm,
    EmptyState,
    Notification
  },
  computed: {
    getTitle() {
      return this.$route.meta.name;
    }
  }
})
export default class StationTransitInDest extends Vue {
  mounted() {
    PodDexController.setType("DEL-DEX");
    this.refs = this.$refs;
  }
  unmounted() {
    PodDexController.onReset();
  }

  // update list
  isUpdateNotification = false;

  refs: any = "";
  fetchPodDexList() {
    PodDexController.fetchPodDexList();
  }

  get isFirstRequest() {
    return PodDexController.isFirstRequest;
  }

  // loading
  get isLoading() {
    return PodDexController.isLoading;
  }

  // filter date
  get startDate() {
    return PodDexController.filter.startDate;
  }

  get endDate() {
    return PodDexController.filter.endDate;
  }

  setDateRange(value: Array<any>) {
    PodDexController.setStartDate(value[0]);
    PodDexController.setEndDate(value[1]);
    value[0] && value[1] && this.fetchPodDexList();
  }

  // filter search
  get searchValue() {
    return PodDexController.filter.search;
  }

  onSearch(value: string) {
    PodDexController.setSearch(value);
    PodDexController.setPagination(new Pagination(1, 10));
    this.fetchPodDexList();
  }

  clearSearch() {
    PodDexController.setSearch("");
    this.fetchPodDexList();
  }

  // Table
  get columns() {
    const columns = [
      {
        name: this.$t("podDex.columns.no"),
        styleHead: "w-24 text-left whitespace-no-wrap",
        render: (item: any, index: number) => {
          return `<div class="overflow-ellipsis text-left">${index + 1}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.noStt"),
        styleHead: "w-50 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left">${item.sttNo}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.noReferensi"),
        styleHead: "w-50 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left">${item.sttNoRefExternal ||
            "-"}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.totalKoli"),
        styleHead: "w-28 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left">${formatByCountry(
            item.totalPiece,
            { type: "quantity" }
          )}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.beratDikenakanBiaya"),
        styleHead: "w-50 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left" data-testid="incoming-pod-dex-chargeable-weight">${formatByCountry(
            item.chargeableWeight,
            { type: "weight", suffix: " Kg" }
          )}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.produk"),
        styleHead: "w-48 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="text-black-lp-300 flex">
                  <div class="rounded px-2 py-0 bg-gray-lp-400">
                      ${item.productName}
                  </div>
                </div>`;
        }
      },
      {
        name: this.$t("podDex.columns.statusTerakhir"),
        styleHead: "w-48 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="text-black-lp-300 flex">
                  <div class="rounded px-2 py-0 bg-gray-lp-400">
                      ${item.lastStatus}
                  </div>
                </div>`;
        }
      },
      {
        name: this.$t("podDex.columns.komoditas"),
        styleHead: "w-48 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left">${item.commodityName}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.biayaCod"),
        styleHead: "w-50 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left" data-testid="incoming-pod-dex-cod-amount">${this.formatMoney(
            item.codAmount
          )}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.tanggalDel"),
        styleHead: "w-50 text-left",
        render: (item: any) => {
          return `<div class="text-black-lp-300">${formatDateWithoutTime(
            item.deliveryDate
          )}</div>
            <div class="capitalize text-gray-lp-500">${formatTimeCustom({
              date: item.deliveryDate,
              isSecond: false
            })}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.namaKurir"),
        styleHead: "w-48 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left">${item.courierName}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.noTelpKurir"),
        styleHead: "w-40 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left">${item.courierPhoneNumber}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.dikirimOleh"),
        styleHead: "w-50 text-left",
        vtooltip: (item: any) =>
          item.deliveredBy?.length > 34 ? item.deliveredBy : "",
        render: (item: any) => {
          return `<div class="text-black-lp-300 uppercase">${ellipsisString(
            item.deliveredBy,
            34
          )}</div>
            <div class="capitalize text-gray-lp-500">${
              item.deliveredByType
            }</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.pengirimanKe"),
        styleHead: "w-48 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left">${formatByCountry(
            item.sttDeliveryAttempt,
            { type: "quantity" }
          )}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.action"),
        key: "actionable_column",
        styleHead: "w-56 text-left",
        showButton: (item: any) => item.lastStatus === "DEL"
      }
    ];
    if (!isMigrateFromElexys()) {
      columns.splice(1, 1);
    }
    return columns;
  }

  // custom button
  isPaid = true;
  onClosePaidStatus() {
    this.isPaid = true;
  }
  get customButton() {
    return {
      outline: false,
      title: this.$t("podDex.updateButton") as string,
      textColor: "white",
      color: "red-lp-100",
      small: false,
      customClass: "whitespace-no-wrap",
      disabled: !FlagsPermissionPodDex.pod_dex_update.isEnabled(),
      clickFunction: (item: any) => this.setUpdate(item, true)
    }
  };

  get pagination() {
    return PodDexController.podDexList.pagination;
  }

  get podDexList() {
    return PodDexController.podDexList.data;
  }

  // Update
  isUpdate = false;
  async setUpdate(item: any, value: boolean) {
    if (!value && !PodDexController.isConfirm) {
      PodDexController.setResetPodDexDetailData();
    } else {
      await PodDexController.checkPaymentStatus({ sttNo: item.sttNo });
      PodDexController.setPodDexDetailData(item);
    }
    if (PodDexController.isPaid) {
      this.isUpdate = value;
    } else {
      this.isPaid = PodDexController.isPaid;
    }
  }
  get detailData() {
    return PodDexController.podDexDetailData;
  }

  // Sent
  sentValue = "";
  get sentList() {
    return [
      {
        value: "",
        name: this.$t("podDex.update.pilih")
      },
      {
        value: "Ya",
        name: this.$t("podDex.update.ya")
      },
      {
        value: "Tidak",
        name: this.$t("podDex.update.tidak")
      }
    ];
  }
  sentSelect = false;
  onOpenDeliveredBySelect(value: boolean) {
    this.sentSelect = value;
  }
  // handle error
  get isError(): boolean {
    return PodDexController.isError;
  }
  get errorCause() {
    return PodDexController.errorCause;
  }
  // filter status
  statusSelect = false;
  get statusData() {
    return [
      { name: this.$t("podDex.statusLatest") as string, value: "" },
      { name: this.$t("podDex.statusDel") as string, value: "DEL" },
      { name: this.$t("podDex.statusDex") as string, value: "DEX" }
    ]
  };
  onOpenStatusSelect() {
    this.statusSelect = true;
  }
  onCloseStatusSelect() {
    this.statusSelect = false;
  }
  statusName = "";
  status = "";
  onSelectStatus(name: any, value: any) {
    PodDexController.setPagination(new Pagination(1, 10));
    PodDexController.selectStatus(value);
    this.onCloseStatusSelect();
    this.statusName = name;
  }

  formatMoney(value: number): string {
    const country = resolveCountry();
    const prefix = country === "MY" ? "RM" : "Rp";
    return `${prefix}${formatByCountry(value, { type: "money" })}`;
  }
}
